import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Search, Megaphone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { usePageSeo } from "@/hooks/usePageSeo";

const LocationsHubPage = () => {
  // Idioma y canonical derivados de la URL: /servicios-locales-houston (ES)
  // y /en/local-services-houston (EN) son páginas distintas, no la misma.
  const { language, url: canonicalUrl, onLanguageChange } = usePageSeo();

  const locations = [
    { name: "Houston", slug: "houston", population: "2.3M+", zip: "77001-77099" },
    { name: "The Woodlands", slug: "the-woodlands", population: "116K+", zip: "77380-77389" },
    { name: "Spring", slug: "spring", population: "62K+", zip: "77373, 77379, 77386" },
    { name: "Conroe", slug: "conroe", population: "98K+", zip: "77301-77306" },
    { name: "Katy", slug: "katy", population: "21K+", zip: "77449, 77450, 77493" },
    { name: "Sugar Land", slug: "sugar-land", population: "111K+", zip: "77478-77479, 77487" },
    { name: "Tomball", slug: "tomball", population: "12K+", zip: "77375, 77377" },
    { name: "Magnolia", slug: "magnolia", population: "2K+", zip: "77353-77355" },
    { name: "Memorial", slug: "memorial", population: "35K+", zip: "77024, 77079" },
  ];

  const content = {
    es: {
      title: "Servicios de SEO y Google Ads por Localidad en Houston | Luis ROC",
      description: "Agencia de marketing digital con presencia local en Houston, The Woodlands, Spring, Conroe, Katy, Sugar Land y más. SEO local y Google Ads optimizados para tu ciudad.",
      h1: "Marketing Digital Local en el Área de Houston",
      intro: "Servimos a empresas en toda el área metropolitana de Houston con estrategias de SEO y Google Ads adaptadas a cada localidad. Conocemos tu mercado local, competencia y audiencia específica.",
      seoCard: "SEO Local",
      seoDesc: "Posicionamiento en Google Maps y búsquedas locales",
      adsCard: "Google Ads",
      adsDesc: "Campañas PPC con segmentación local precisa",
      viewSEO: "Ver SEO en",
      viewAds: "Ver Google Ads en",
      coverage: "Cobertura de Servicio",
      allLocations: "Todas las Localidades",
      cta: "¿Tu ciudad no aparece? Contáctanos →"
    },
    en: {
      title: "SEO and Google Ads Services by Location in Houston | Luis ROC",
      description: "Digital marketing agency with local presence in Houston, The Woodlands, Spring, Conroe, Katy, Sugar Land and more. Local SEO and optimized Google Ads for your city.",
      h1: "Local Digital Marketing in the Houston Area",
      intro: "We serve businesses throughout the Houston metro area with SEO and Google Ads strategies tailored to each location. We know your local market, competition and specific audience.",
      seoCard: "Local SEO",
      seoDesc: "Positioning in Google Maps and local searches",
      adsCard: "Google Ads",
      adsDesc: "PPC campaigns with precise local targeting",
      viewSEO: "View SEO in",
      viewAds: "View Google Ads in",
      coverage: "Service Coverage",
      allLocations: "All Locations",
      cta: "Your city not listed? Contact us →"
    }
  };

  const t = content[language];

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{t.title}</title>
        <meta name="description" content={t.description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="keywords" content="seo houston, google ads houston, marketing digital local, seo the woodlands, seo spring, seo conroe, seo katy, seo sugar land" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />
        
        <main className="container mx-auto px-4 py-12">
          <BreadcrumbNav 
            items={language === 'es'
              ? [
                  { name: 'Servicios', url: '/servicios' },
                  { name: 'Localidades', url: '/servicios-locales-houston' }
                ]
              : [
                  { name: 'Services', url: '/en/services' },
                  { name: 'Locations', url: '/en/local-services-houston' }
                ]}
            language={language}
          />


          <section className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              {t.h1}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              {t.intro}
            </p>
          </section>

          <section className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-12">{t.allLocations}</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {locations.map((location) => (
                <Card key={location.slug} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-primary" />
                      {location.name}
                    </CardTitle>
                    <CardDescription>
                      {location.population} • ZIP {location.zip}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Link to={`/seo-${location.slug}`}>
                        <Button variant="outline" className="w-full justify-between group">
                          <span className="flex items-center gap-2">
                            <Search className="h-4 w-4" />
                            {t.viewSEO} {location.name}
                          </span>
                          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                      
                      <Link to={`/google-ads-${location.slug}`}>
                        <Button variant="outline" className="w-full justify-between group">
                          <span className="flex items-center gap-2">
                            <Megaphone className="h-4 w-4" />
                            {t.viewAds} {location.name}
                          </span>
                          <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section className="bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg p-12 text-center">
            <h2 className="text-3xl font-bold mb-4">{t.cta}</h2>
            <p className="text-xl mb-6 opacity-90">
              {language === 'es' 
                ? 'También servimos otras áreas de Houston y Texas. Contáctanos para una consulta personalizada.'
                : 'We also serve other areas of Houston and Texas. Contact us for a personalized consultation.'}
            </p>
            <Button 
              size="lg" 
              variant="secondary"
              onClick={() => window.location.href = '/contacto'}
            >
              {language === 'es' ? 'Solicitar Consulta' : 'Request Consultation'}
            </Button>
          </section>
        </main>

        <Footer language={language} />
      </div>
    </>
  );
};

export default LocationsHubPage;
